"use client";

import { useCallback, useMemo } from "react";
import { useSignMessageWithConnection } from "@/hooks/core/useSignMessageWithConnection";

/**
 * CGU message that the user signs to acknowledge the automatic streak terms.
 * This message is static and should match what the backend expects.
 */
export const AUTOMATIC_STREAK_CGU_MESSAGE = `I acknowledge and accept the Automatic Streak Terms of Service.

By signing this message, I confirm that:
1. I understand this is a beta feature that may contain bugs. Trading involves financial risk.
2. I authorize Cede Labs to execute automated buy and sell trades on my behalf. Trades will automatically stop when the epoch ends, maximum volume is reached, or a trade fails.
3. I have read and accept the Terms and Conditions.

This signature serves as my cryptographic consent to these terms.`;

/**
 * Generates the CGU message with a nonce for uniqueness.
 * The nonce prevents replay attacks and provides a timestamp for the acceptance.
 */
export const generateCguMessage = (nonce: number): string => {
  return `${AUTOMATIC_STREAK_CGU_MESSAGE}\n\nNonce: ${nonce}`;
};

/**
 * Hook for signing the Automatic Streak CGU acceptance message.
 * Uses useSignMessageWithConnection to ensure wallet is connected before signing.
 */
export const useSignAutomaticStreakCgu = () => {
  const { signMessageAsync, isSigning } = useSignMessageWithConnection();

  const signCguAcceptance = useCallback(async (): Promise<{ signature: string; nonce: number }> => {
    const nonce = Date.now();
    const message = generateCguMessage(nonce);

    const signature = await signMessageAsync({ message });

    return {
      signature,
      nonce,
    };
  }, [signMessageAsync]);

  return useMemo(
    () => ({
      signCguAcceptance,
      isSigning,
    }),
    [signCguAcceptance, isSigning],
  );
};
